/**
 * The pin gesture — ADR-022 D1 made touchable: *click the model at the
 * fingertip; name what you hit.*
 *
 * A sibling of the pose tool, deliberately built to its shape: the same 5px
 * travel guard (a press that travelled is a camera move, not a pick), the same
 * "the pick surface is the thing itself" posture, and the same rule that a
 * gesture is OFFERED rather than guarded against (ADR-006 §13.2 — mode scopes
 * tools). The difference is what it picks: the pose tool picks bones off the
 * skeleton visual, and this picks a PLACE on the mesh.
 *
 * IT DECIDES NOTHING ABOUT THE PLACE. Whether the click landed on the hand
 * being pinned, whether a digit name should be proposed for it, whether it
 * corroborates anything — all of that belongs to the panel and to
 * `hands/digit-pins`. This converts a screen position into a bind-world point
 * (via `hands/pin-gesture`, which owns that conversion and its refusals) and
 * hands it over.
 *
 * A MISS IS REPORTED, NOT SWALLOWED. Clicking past the model is the single most
 * likely thing to happen when a hand is forty pixels across, and a gesture that
 * silently does nothing teaches a person that pinning is broken. `onMiss` is
 * how the panel gets to say "that click missed the model — try again closer to
 * the fingertip", which is a sentence, not a mystery.
 *
 * Depends on: three (pointer plumbing only), hands/pin-gesture (the conversion).
 * Depended on by: viewport (offers/withdraws it).
 */
import { Raycaster, Vector2, type Camera, type Object3D } from 'three';
import { pinHitAt, PinGestureRefusal, type PinHit } from '../hands/pin-gesture.js';

export interface PinToolConfig {
  /**
   * A trusted click on an existing MARKER — a candidate disc or a placed pin.
   * Tested BEFORE the mesh, because the disc is the target a person aims at:
   * Allen's instinct on first use was to click the discs, and the measurement
   * agrees (a ray 3 mm from a fingertip hits no geometry at all).
   */
  onMarker(handBone: string, markerId: string): void;
  /** The marker under the pointer, or null — rollover, so the target is findable. */
  onHover(handBone: string | null, markerId: string | null): void;
  /** A trusted click that landed on the subject's MESH, in BIND-WORLD. */
  onPin(hit: PinHit): void;
  /** A trusted click that hit nothing — so the surface can say so. */
  onMiss(): void;
  /**
   * A click that hit geometry the conversion cannot honestly carry back to bind
   * (a degenerate triangle, a faceless drawable). Named, never silent.
   */
  onRefusal(message: string): void;
  /**
   * The marker discs, asked for at pick time — they are rebuilt on every panel
   * render, so a captured list would point at disposed objects. It comes from
   * the CALLER rather than the viewport, because the marker layer belongs to
   * the panel that owns those pins, not to the scene.
   */
  markers(): readonly Object3D[];
}

export interface PinToolDeps {
  readonly camera: Camera;
  readonly domElement: HTMLElement;
  /** The SUBJECT root — not the whole scene, which also holds markers and gizmos. */
  readonly subject: Object3D;
}

export interface PinTool {
  readonly probe: { pinned: number; missed: number; markers: number };
  dispose(): void;
}

/** Travel beyond this many pixels between press and release is an orbit, not a pick. */
const CLICK_TRAVEL_PX = 5;

export function createPinTool(deps: PinToolDeps, config: PinToolConfig): PinTool {
  const probe = { pinned: 0, missed: 0, markers: 0 };
  let pressAt: { x: number; y: number } | null = null;
  const raycaster = new Raycaster();
  /**
   * The marker under the pointer, keyed by HAND AND ID (Codex P2, #94). Marker
   * ids are minted per hand, so `tip-1` exists on both — an id-only cache
   * suppressed the hover event when the pointer crossed from one hand's `tip-1`
   * to the other's, leaving the first hand lit and the second dead.
   */
  let hovered: string | null = null;

  /** NDC for a pointer position over the canvas. */
  const ndcOf = (event: { clientX: number; clientY: number }): Vector2 => {
    const rect = deps.domElement.getBoundingClientRect();
    return new Vector2(
      ((event.clientX - rect.left) / rect.width) * 2 - 1,
      -((event.clientY - rect.top) / rect.height) * 2 + 1,
    );
  };

  /** The marker disc under a pointer position, if any. */
  const markerAt = (event: { clientX: number; clientY: number }): Object3D | null => {
    const pickables = config.markers();
    if (pickables.length === 0) return null;
    // ★ SEAT THEM BEFORE AIMING AT THEM. The discs are rebuilt on every panel
    //   render — including the render this tool's own hover triggers — and a
    //   freshly constructed Object3D carries an IDENTITY `matrixWorld` until the
    //   next frame updates the graph. `Raycaster` reads `matrixWorld` and asks
    //   nobody to refresh it, so a click landing in the same frame as a redraw
    //   was tested against discs still sitting at the world origin: every pick
    //   missed, the gesture fell through to the mesh, and the panel said "that
    //   click missed the model". MEASURED — the identical raycast run one frame
    //   later hits. Same class as the stale bounding sphere in `pin-gesture`:
    //   three caches a transform and expects the app to know when it is stale.
    for (const object of pickables) object.updateWorldMatrix(true, false);
    raycaster.setFromCamera(ndcOf(event), deps.camera);
    return raycaster.intersectObjects([...pickables], false)[0]?.object ?? null;
  };

  const onPointerMove = (event: PointerEvent): void => {
    const marker = markerAt(event);
    const hand = marker === null ? null : (marker.userData['handBone'] as string);
    const id = marker === null ? null : (marker.userData['markerId'] as string);
    const key = marker === null ? null : `${hand}:${id}`;
    if (key === hovered) return; // no redraw churn while the pointer travels
    hovered = key;
    config.onHover(hand, id);
  };

  const onPointerDown = (event: PointerEvent): void => {
    pressAt = { x: event.clientX, y: event.clientY };
  };

  const onPointerUp = (event: PointerEvent): void => {
    const start = pressAt;
    pressAt = null;
    if (!start || Math.hypot(event.clientX - start.x, event.clientY - start.y) > CLICK_TRAVEL_PX) return;
    // THE TRUSTED-GESTURE RULE, at the gesture rather than in a wrapper (brief
    // §5.6). A pin is a human act — it is the ONLY thing that can put a digit
    // on a hand under ADR-022 §9 — so a script dispatching a synthetic click
    // must not be able to place one. The panel checks again where it mints
    // `confirmedBy`; this is the earlier of the two doors, and both are needed:
    // this one keeps a forged click from ever becoming a dot on screen.
    if (!event.isTrusted) return;

    // THE DISCS COME FIRST. They are drawn over the mesh (depthTest off) and
    // they are what a person is actually pointing at, so a click that lands on
    // one is about that marker — never a new pin a few millimetres away from it.
    const marker = markerAt(event);
    if (marker !== null) {
      probe.markers += 1;
      config.onMarker(marker.userData['handBone'] as string, marker.userData['markerId'] as string);
      return;
    }

    const ndc = ndcOf(event);
    let hit: PinHit | null;
    try {
      hit = pinHitAt(deps.subject, deps.camera, ndc);
    } catch (err) {
      if (err instanceof PinGestureRefusal) {
        config.onRefusal(err.message);
        return;
      }
      throw err;
    }
    if (hit === null) {
      probe.missed += 1;
      config.onMiss();
      return;
    }
    probe.pinned += 1;
    config.onPin(hit);
  };

  deps.domElement.addEventListener('pointerdown', onPointerDown);
  deps.domElement.addEventListener('pointerup', onPointerUp);
  deps.domElement.addEventListener('pointermove', onPointerMove);
  // The cursor IS the mode indicator: a crosshair over the model is how a
  // person knows the next click will place something rather than orbit.
  const previousCursor = deps.domElement.style.cursor;
  deps.domElement.style.cursor = 'crosshair';

  return {
    probe,
    dispose() {
      deps.domElement.removeEventListener('pointerdown', onPointerDown);
      deps.domElement.removeEventListener('pointerup', onPointerUp);
      deps.domElement.removeEventListener('pointermove', onPointerMove);
      deps.domElement.style.cursor = previousCursor;
    },
  };
}
